import { z } from "zod";
import { useUser } from "@/features/user";
import { useError } from "@/providers/Error";
import { genericMessages } from "@/constants/errorMessages";
import { getIncomeIssue } from "./incomeValidation";

export const declaredIncomeSchema = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    const issue = getIncomeIssue(value);
    if (issue) {
      ctx.addIssue({ code: "custom", message: issue.message });
    }
  })
  .transform(Number);

// Saved before the BC score is generated: the backend freezes the declared
// income into the score, and the preliminary offer is priced on it.
export const useSubmitIncome = ({
  onError,
}: { onError?: (message: string) => void } = {}) => {
  const { setError } = useError();
  // The chat answers errors in the conversation instead of the popover.
  const reportError = onError ?? setError;
  const { getUser, updatePersonalData, isUpdatePending } = useUser();

  // Resolves true once the income is saved on the signed-in user.
  const submitIncome = async (declaredIncome: number) => {
    try {
      const result = await updatePersonalData({ declaredIncome });
      if (!result.isSuccess) {
        reportError(genericMessages.unexpected);
        return false;
      }

      // The guards read the income from the signed-in user.
      if (!result.user) {
        await getUser();
      }
      return true;
    } catch {
      reportError(genericMessages.unexpected);
      return false;
    }
  };

  return { submitIncome, isLoading: isUpdatePending };
};
